import { joinGroup } from '@echo/api-client/onearly';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { AS_PERSON, asMemberOf, type WriteScope } from '../content/scope';
import {
  ensureGroupMembership,
  type GroupMembership,
  isGroupLeader,
} from '../domain/group-membership';
import { queryKeys } from '../query/keys';
import { useConfig, useSession } from './provider';

/** The join call, once per session: shared by `useGlobalGroup` and `useWriteScope`. */
function groupMembershipQuery(groupActorUuid: string) {
  return {
    queryKey: queryKeys.groupMembership(groupActorUuid),
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: (): Promise<GroupMembership> => ensureGroupMembership(() => joinGroup(groupActorUuid)),
  };
}

/**
 * Makes sure the signed-in person belongs to the global Stronger Memory group
 * (`strongerMemoryGroupActorUuid` in the env config). Runs once per session; the join
 * is idempotent on Core's side, so re-running after a token refresh is harmless.
 *
 * Mount it once at each app's root, after the session gate. Screens don't wait on it, and
 * `unavailable` only means the environment has no group yet. Saving a result does wait for it
 * (`useWriteScope`), to know who to write the result as.
 */
export function useGlobalGroup() {
  const session = useSession();
  const { strongerMemoryGroupActorUuid: groupActorUuid } = useConfig();
  return useQuery({
    ...groupMembershipQuery(groupActorUuid ?? 'none'),
    enabled: session.status === 'authenticated' && groupActorUuid !== undefined,
  });
}

/**
 * Who results are written as now (`content/scope.ts`): a member of the global group once the
 * join says the person is in it, the person alone where there is no group or it is unavailable.
 * Waits for the join rather than guessing; a join that fails throws, so the save waits too.
 */
export function useWriteScope(): () => Promise<WriteScope> {
  const queryClient = useQueryClient();
  const { strongerMemoryGroupActorUuid: groupActorUuid } = useConfig();
  return useCallback(async () => {
    if (!groupActorUuid) return AS_PERSON;
    const membership = await queryClient.ensureQueryData(groupMembershipQuery(groupActorUuid));
    return membership.status === 'member' ? asMemberOf(groupActorUuid) : AS_PERSON;
  }, [queryClient, groupActorUuid]);
}

/** Whether the signed-in person leads the global group (Core's Admin role), from the same join call. */
export function useGroupRole(): { isLeader: boolean; isKnown: boolean } {
  const membership = useGlobalGroup();
  return { isLeader: isGroupLeader(membership.data), isKnown: membership.data !== undefined };
}
